import { useTranslation } from 'react-i18next';
import { ShoppingBag, Wallet, TrendingUp, Users } from 'lucide-react';
import { ICON_BG } from './constants';

const STAT_ITEMS = [
  { key: 'orders',    icon: ShoppingBag, color: 'blue' },
  { key: 'sales',     icon: Wallet,      color: 'emerald' },
  { key: 'profit',    icon: TrendingUp,  color: 'violet' },
  { key: 'customers', icon: Users,       color: 'amber' },
];

function StatTile({ icon: Icon, color, label, value, trend }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <div className={`w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br ${ICON_BG[color]} mb-2`}>
        <Icon size={12} className="text-white" />
      </div>
      <p className="text-[10px] text-gray-400 leading-tight">{label}</p>
      <p className="text-sm font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
      <p className="text-[10px] font-semibold text-emerald-500 mt-0.5">{trend}</p>
    </div>
  );
}

function MiniLineChart({ gradientId, size = 'sm' }) {
  return (
    <svg
      viewBox="0 0 300 100"
      preserveAspectRatio="none"
      className={`w-full ${size === 'lg' ? 'h-32 sm:h-40' : 'h-16 sm:h-20'}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0,80 L40,65 L80,70 L120,45 L160,55 L200,30 L240,40 L300,15 L300,100 L0,100 Z"
        fill={`url(#${gradientId})`}
      />
      <polyline
        points="0,80 40,65 80,70 120,45 160,55 200,30 240,40 300,15"
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BarRow({ label, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-400">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Toast({ title, body }) {
  return (
    <div
      className="absolute -bottom-6 -left-4 sm:-left-8 max-w-[200px] sm:max-w-[230px] bg-white rounded-2xl shadow-xl border border-gray-100 p-3.5 animate-fade-in-up"
      style={{ animationDelay: '600ms' }}
    >
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{body}</p>
    </div>
  );
}

export default function DashboardMock({ variant = 'hero' }) {
  const { t } = useTranslation();

  if (variant === 'progress') {
    return (
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-5 sm:p-7">
        <p className="text-xs text-gray-400">{t('landing.progress.mock.totalLabel')}</p>
        <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-1 mb-4">
          {t('landing.progress.mock.totalValue')}
        </p>
        <MiniLineChart gradientId="lineGrad-progress" size="lg" />
        <div className="mt-6 space-y-4">
          <p className="text-xs font-semibold text-gray-500">{t('landing.progress.mock.topProductsLabel')}</p>
          <BarRow label={t('landing.progress.mock.products.p1')} pct={86} color="from-violet-500 to-purple-500" />
          <BarRow label={t('landing.progress.mock.products.p2')} pct={64} color="from-blue-500 to-indigo-500" />
          <BarRow label={t('landing.progress.mock.products.p3')} pct={42} color="from-amber-500 to-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-bold text-gray-800">{t('landing.dashboardMock.greeting')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('landing.dashboardMock.subtitle')}</p>
          </div>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/10" />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {STAT_ITEMS.map(({ key, icon, color }) => (
            <StatTile
              key={key}
              icon={icon}
              color={color}
              label={t(`landing.dashboardMock.stats.${key}.label`)}
              value={t(`landing.dashboardMock.stats.${key}.value`)}
              trend={t(`landing.dashboardMock.stats.${key}.trend`)}
            />
          ))}
        </div>

        <div className="bg-gray-50 rounded-2xl p-4">
          <p className="text-xs text-gray-400">{t('landing.dashboardMock.chart.label')}</p>
          <p className="text-lg font-bold text-gray-800 mt-0.5 mb-2">৳{t('landing.dashboardMock.chart.value')}</p>
          <MiniLineChart gradientId="lineGrad-hero" size="sm" />
        </div>
      </div>

      <Toast title={t('landing.dashboardMock.toast.title')} body={t('landing.dashboardMock.toast.body')} />
    </div>
  );
}
